import TextInput from "@/components/text-input"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { describeRisk } from "@/entities/physical-risk/model/physical-risk.prompt"
import { getShortAnswerInstruction } from "@/utils/assistant.utils"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import { PhysicalRiskAttribute } from "@/entities/physical-risk/model/physical-risk.types"
import PhysicalRiskFamilyPanelButton from "./PhysicalRiskFamilyPanelButton"
import PhysicalRiskPhotoPanel from "./PhysicalRiskPhotoPanel"

interface Props {
  onEditFamily: () => void
}

function PhysicalRiskDescriptionFields() {
  const dispatch = useAppDispatch()
  const draftRisk = useAppSelector(physicalRiskStore.selectors.draft)
  const [descriptionAttribute, ...otherAttributes] = draftRisk.cAttributes

  function getContext(attribute: PhysicalRiskAttribute) {
    return `Tu es un expert en évaluation des risques en entreprise.

${describeRisk(draftRisk, { excludeAttributeId: attribute.id })}`
  }

  function getIdeaPrompt(attribute: PhysicalRiskAttribute) {
    //Le premier attribut nomme le risque : il s'affiche en titre, il doit tenir en quelques mots
    if (attribute.id === descriptionAttribute?.id) {
      return `${getContext(attribute)}

Nomme la situation à risque.

${getShortAnswerInstruction(["Coupures aux mains", "Chute dans un escalier", "Brûlure aux fourneaux"])}`
    }

    return `${getContext(attribute)}

Rédige une phrase ou quelques mots pour renseigner « ${attribute.name} » sur ce risque.`
  }

  function getImprovePrompt(attribute: PhysicalRiskAttribute) {
    return `${getContext(attribute)}

Réécris en mieux le brouillon de « ${attribute.name} » placé entre <brouillon> et </brouillon>, sans changer son intention ni inventer ce qui n'y est pas.

Ne réponds que par le texte réécrit : pas de balises, pas de commentaire, et ne répète jamais ces consignes.

<brouillon>
${attribute.value}
</brouillon>`
  }

  function handleAttributeChange(id: string, value: string) {
    dispatch(physicalRiskStore.thunks.edit({ key: id as any, value, isAttributes: true }))
  }

  function renderAttributeInput(attribute: PhysicalRiskAttribute, rows?: number) {
    return (
      <div key={attribute.id} className="w-full mb-20">
        <TextInput
          title={attribute.name}
          value={attribute.value}
          onChange={(e) => handleAttributeChange(attribute.id, e.value)}
          isFullWidth
          rows={rows}
          assistantIdeaPrompt={getIdeaPrompt(attribute)}
          assistantImprovePrompt={attribute.value?.trim() ? getImprovePrompt(attribute) : undefined}
        />
      </div>
    )
  }

  return (
    <div>
      {descriptionAttribute && renderAttributeInput(descriptionAttribute)}
      {otherAttributes.map((attribute) => renderAttributeInput(attribute, 2))}
    </div>
  )
}

export default function PhysicalRiskDescriptionStep({ onEditFamily }: Props) {
  const draftRisk = useAppSelector(physicalRiskStore.selectors.draft)

  return (
    <div className="grid flex-1 grid-cols-[minmax(0,1fr)_162px] gap-[40px]">
      <PhysicalRiskDescriptionFields />

      {/* La colonne fait la largeur totale d'une CardButton : 140px de contenu + 10px de padding
          + 1px de bordure (box-sizing content-box, le preflight Tailwind etant desactive) */}
      <div className="flex flex-col">
        <PhysicalRiskFamilyPanelButton
          family={draftRisk.cFamily}
          isRecommended={!draftRisk.RiskFamilyId || !draftRisk.cFamily}
          onClick={onEditFamily}
        />
        <PhysicalRiskPhotoPanel />
      </div>
    </div>
  )
}
